import React, { useEffect, useState } from "react";
import { updateRoute } from "../../api/routes";
import { routeErrorText, isStopsRemovalConflict } from "../../utils/routeApiErrors";
import { useTranslation } from "react-i18next";
import {
  ArrowRight,
  ArrowLeft,
  Search,
  Building2,
  MapPin,
  Clock,
  Check,
  X
} from "lucide-react";
import useRoutes from "../../store/routes";
import { Button } from "../ui/Button";
import { Skeleton } from "../ui/Skeleton";
import { toast } from "../../utils/toast";
import useShippingAddressStore from "store/shippingAddress";
import './editRouteClients.scss';

const ClientList = ({
  title,
  clients,
  selectedClients,
  handleCheckboxChange,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const { selectedShippingAddresses, setShippingAddress } =
    useShippingAddressStore();

  const filteredClients =
    query !== ""
      ? clients.filter(
          (client) =>
            (client.legalName ?? client.name)?.toLowerCase().includes(query.toLowerCase()) ||
            client.shippingAddresses?.some((address) =>
              address.address?.toLowerCase().includes(query.toLowerCase())
            )
        )
      : clients;

  return (
    <div className="edit-route-clients__list">
      <div className="edit-route-clients__list-header">
        <h3 className="edit-route-clients__list-title">
          {title}
          <span className="edit-route-clients__list-badge">
            {clients.length}
          </span>
        </h3>
        <div className="edit-route-clients__search">
          <Search size={18} className="edit-route-clients__search-icon" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("Buscar por nombre o dirección...")}
            className="edit-route-clients__search-input"
            aria-label={t("Buscar en") + ` ${title}`}
            style={{ paddingLeft: '3rem' }}
          />
        </div>
      </div>
      <div className="edit-route-clients__list-content">
        {isLoading ? (
          <div className="edit-route-clients__loading" aria-busy="true" aria-live="polite">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="edit-route-clients__skel-row">
                <Skeleton className="edit-route-clients__skel-check" />
                <div className="edit-route-clients__skel-lines">
                  <Skeleton className="edit-route-clients__skel-line edit-route-clients__skel-line--name" />
                  <Skeleton className="edit-route-clients__skel-line edit-route-clients__skel-line--addr" />
                </div>
              </div>
            ))}
            <span className="edit-route-clients__sr-only">{t("Cargando clientes...")}</span>
          </div>
        ) : filteredClients.length === 0 ? (
          <div className="edit-route-clients__empty">
            <Building2 size={32} className="edit-route-clients__empty-icon" />
            <p className="edit-route-clients__empty-text">
              {query ? t('No se encontraron clientes') : t('No hay clientes en esta lista')}
            </p>
          </div>
        ) : (
          <ul className="edit-route-clients__client-list">
            {filteredClients.map((client) => {
              const displayName = client?.legalName || client?.name || t("Sin nombre");
              const pickUpTime = client?.pickUpTime || t("No especificado");
              const isSelected = selectedClients.includes(client._id);
              const hasAddresses = client.shippingAddresses?.filter((a) => !!a.address).length > 0;
              const hasMultipleAddresses = client.shippingAddresses?.filter((a) => !!a.address).length > 1;
              const selectedAddressId = client.selectedAddress || selectedShippingAddresses[client._id] || "";

              return (
                <li
                  key={client._id}
                  className={`edit-route-clients__client-item ${isSelected ? 'selected' : ''}`}
                >
                  <div className="edit-route-clients__client-checkbox-wrapper">
                    <label className="edit-route-clients__client-checkbox-label">
                      <input
                        type="checkbox"
                        disabled={!hasAddresses}
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(client._id)}
                        className="edit-route-clients__client-checkbox"
                        aria-label={t("Seleccionar {{displayName}}", { displayName })}
                      />
                      <span className="edit-route-clients__client-checkbox-custom"></span>
                    </label>
                    <div className="edit-route-clients__client-info">
                      <h4 className="edit-route-clients__client-name" title={displayName}>
                        {displayName}
                      </h4>
                      {!hasAddresses && (
                        <span className="edit-route-clients__client-warning">
                          {t("Sin direcciones")}
                        </span>
                      )}
                    </div>
                  </div>

                  {hasAddresses && (
                    <div className="edit-route-clients__client-details">
                      <div className="edit-route-clients__client-time">
                        <Clock size={14} className="edit-route-clients__client-time-icon" />
                        <span className="edit-route-clients__client-time-text">
                          {t("Hora de recogida")}: {pickUpTime}
                        </span>
                      </div>

                      {hasMultipleAddresses ? (
                        <div className="edit-route-clients__address-select">
                          <label className="edit-route-clients__address-select-label">
                            <MapPin size={14} />
                            <span>{t("Dirección de entrega")}</span>
                          </label>
                          <select
                            value={selectedAddressId}
                            onChange={(e) =>
                              setShippingAddress(client._id, e.target.value)
                            }
                            className="edit-route-clients__address-select-input"
                            aria-label={t("Seleccionar dirección")}
                          >
                            <option value="" disabled>
                              {t("Seleccionar dirección")}
                            </option>
                            {client.shippingAddresses
                              .filter((address) => address.address)
                              .map((address, index) => (
                                <option key={address._id || index} value={address._id}>
                                  {address.address}, {address.municipality || t("Sin comuna")} ({address.type || t("Sin tipo")})
                                </option>
                              ))}
                          </select>
                        </div>
                      ) : (
                        <div className="edit-route-clients__address-display">
                          <MapPin size={14} className="edit-route-clients__address-icon" />
                          <span className="edit-route-clients__address-text">
                            {client.shippingAddresses[0].address || t("Sin dirección")},{" "}
                            {client.shippingAddresses[0].municipality || t("Sin comuna")}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const ActionButtons = ({ moveClientsToAssigned, moveClientsToAvailable, canMoveToAssigned, canMoveToAvailable }) => {
  const { t } = useTranslation();

  return (
    <div className="edit-route-clients__actions">
      <button
        type="button"
        onClick={moveClientsToAssigned}
        disabled={!canMoveToAssigned}
        className={`edit-route-clients__action-btn edit-route-clients__action-btn--right ${!canMoveToAssigned ? 'disabled' : ''}`}
        aria-label={t("Mover clientes seleccionados a la ruta")}
        title={t("Agregar a la ruta")}
      >
        <ArrowRight size={24} />
      </button>
      <button
        type="button"
        onClick={moveClientsToAvailable}
        disabled={!canMoveToAvailable}
        className={`edit-route-clients__action-btn edit-route-clients__action-btn--left ${!canMoveToAvailable ? 'disabled' : ''}`}
        aria-label={t("Quitar clientes seleccionados de la ruta")}
        title={t("Quitar de la ruta")}
      >
        <ArrowLeft size={24} />
      </button>
    </div>
  );
};

export default function EditRouteClients({ routeData, closeModal }) {
  const { t } = useTranslation();
  const clients = useRoutes((state) => state.clients || []);
  const fetchRoutes = useRoutes((state) => state.fetchRoutesBeartrack);

  const [availableClients, setAvailableClients] = useState([]);
  const [assignedClients, setAssignedClients] = useState([]);
  const [selectedClients, setSelectedClients] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const { setShippingAddress, selectedShippingAddresses } =
    useShippingAddressStore((state) => state);

  useEffect(() => {
    setIsLoading(true);
    if (routeData && clients.length > 0) {
      const available = clients.filter(
        (client) =>
          !routeData.clients?.some((_client) => _client.id === client._id)
      );
      const assignedIds = routeData.clients?.map((client) => client.id) || [];
      const assigned = clients.filter((client) => assignedIds.includes(client._id));

      setAvailableClients(available);
      setAssignedClients(assigned);

      // Establecer direcciones seleccionadas para clientes asignados
      if (routeData.clients) {
        routeData.clients.forEach((client) => {
          if (client.shippingAddress?.id) {
            setShippingAddress(client.id, client.shippingAddress.id);
          }
        });
      }
    } else {
      setAvailableClients(clients);
      setAssignedClients([]);
    }
    setIsLoading(false);
  }, [routeData, clients, setShippingAddress]);

  /**
   * Datos operativos de la parada que ESTA pantalla no edita pero que viven en
   * `route.clients[]`: ventana de retiro, geocerca, bultos/pedidos/peso esperados y
   * montos. Los carga `/admin/recolecciones`.
   *
   * Hay que reenviarlos tal cual porque `PUT /routes/:id` reemplaza el array `clients`
   * COMPLETO (findOneAndUpdate con $set, no hay merge por parada). Mandando solo
   * `id` + `selectedAddress` —como se hacía— cada guardado desde acá borraba en
   * silencio los diez campos de TODAS las paradas de la ruta, y el operador no tenía
   * forma de enterarse hasta que un retiro salía sin ventana ni geocerca.
   *
   * `routesWithDetails` ya los devuelve en `routeData.clients[]` justamente para esto
   * (ver el bloque `operational` de routes.service.ts).
   */
  const OPERATIONAL_STOP_FIELDS = [
    "window",
    "expectedBulks",
    "expectedOrders",
    "expectedWeightKg",
    "geofencingMeters",
    "geofencingEnabled",
    "clientCharge",
    "driverPayment",
    "freeOrdersThreshold",
    "notes",
  ];

  /**
   * Busca la parada existente para preservar sus datos operativos.
   *
   * Prefiere la coincidencia por empresa + dirección; si no la encuentra (el usuario
   * cambió la dirección de la parada en esta misma pantalla) cae a la primera parada
   * de esa empresa, que es el dato más cercano al que el operador tenía cargado.
   *
   * LIMITACIÓN CONOCIDA: esta pantalla modela la asignación como un conjunto de
   * EMPRESAS (un checkbox por empresa, una dirección elegida), así que no puede
   * representar la misma empresa dos veces con direcciones distintas. Si la ruta las
   * tenía, guardar acá las colapsa en una. Eso ya pasaba antes de este cambio y se
   * arregla rediseñando el componente, no acá.
   */
  const findExistingStop = (companyId, addressId) => {
    const stops = routeData?.clients ?? [];
    const byCompany = stops.filter((stop) => String(stop.id) === String(companyId));
    if (byCompany.length === 0) return null;
    return (
      byCompany.find(
        (stop) => String(stop.shippingAddress?.id ?? stop.shippingAddress) === String(addressId),
      ) ?? byCompany[0]
    );
  };

  const submitChange = async () => {
    setIsLoading(true);
    try {
      const clientsPayload = assignedClients.map((client) => {
        const selectedAddress =
          selectedShippingAddresses[client._id] || client.shippingAddresses?.[0]?._id || "";
        const payload = { id: client._id, selectedAddress };

        const existing = findExistingStop(client._id, selectedAddress);
        if (existing) {
          // Se omiten los ausentes en vez de mandarlos en `null`: el DTO valida por
          // tipo (`@IsNumber`, `@ValidateNested`) y una parada nueva debe recibir los
          // defaults del schema, no nulls explícitos.
          OPERATIONAL_STOP_FIELDS.forEach((field) => {
            const value = existing[field];
            if (value !== null && value !== undefined) payload[field] = value;
          });
        }

        return payload;
      });

      // updateRoute never rejects: it resolves `{ error }`. Rethrow it marked
      // with `apiError` so a backend body can be told apart from a JS bug.
      // Presence, not truthiness: a failed response with an empty body arrives
      // as `error: ''` (axios keeps it as-is) and must not read as a save.
      const save = async (confirmRemoval) => {
        const result = await updateRoute(routeData.id, {
          clients: clientsPayload,
          ...(confirmRemoval ? { confirmRemoval: true } : {}),
        });
        if (result && result.error !== undefined) {
          const error = new Error("route_update_failed");
          error.apiError = result.error;
          throw error;
        }
        return result;
      };

      try {
        await save(false);
      } catch (error) {
        if (!isStopsRemovalConflict(error.apiError)) throw error;

        // The backend sends no `message`, only the stops it would remove.
        // They are mapped to company names so the user knows what is at stake.
        const removedStops = error.apiError?.removedStops ?? [];
        const stopName = (stop) => {
          // A stop can belong to a company with no active contract, so it is
          // missing from the store list; the route snapshot still names it. An
          // ObjectId is never shown to the user.
          const company = clients.find((c) => String(c._id) === String(stop?.id));
          const stopInRoute = routeData?.clients?.find(
            (s) => String(s.id) === String(stop?.id)
          );
          return (
            // `||`, not `??`: the backend sends `legalName: ''` when it is unset.
            company?.legalName ||
            company?.name ||
            stopInRoute?.legalName ||
            stopInRoute?.name ||
            t("Comercio desconocido")
          );
        };
        const names = [...new Set(removedStops.map(stopName))];
        const confirmed = window.confirm(
          t("Vas a quitar {{n}} paradas de la ruta: {{nombres}}. ¿Continuar?", {
            n: removedStops.length,
            nombres: names.join(", "),
          })
        );
        if (!confirmed) return; // no save, no toast: the modal stays open

        await save(true);
      }

      toast.success(t("Ruta actualizada exitosamente!"), { icon: "✅", autoClose: 3000 });
      try {
        await fetchRoutes();
      } catch (error) {
        // The save already succeeded: a failed refresh must not read as a save error.
        console.error("Error al refrescar las rutas:", error);
      }
      closeModal();
    } catch (error) {
      console.error("Error al actualizar la ruta:", error);
      toast.error(
        error?.apiError !== undefined
          ? routeErrorText(error.apiError, t)
          : t("Error al guardar la ruta"),
        { icon: "❌", autoClose: 5000 }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCheckboxChange = (clientId) => {
    setSelectedClients((prevSelected) => {
      if (prevSelected.includes(clientId)) {
        return prevSelected.filter((id) => id !== clientId);
      } else {
        return [...prevSelected, clientId];
      }
    });
  };

  const moveClientsToAssigned = () => {
    const movingClients = availableClients.filter((client) =>
      selectedClients.includes(client._id)
    );
    setAssignedClients([...assignedClients, ...movingClients]);
    movingClients.forEach((client) => {
      if (!selectedShippingAddresses[client._id] && client.shippingAddresses?.length > 0) {
        const firstAddress = client.shippingAddresses.find(a => a.address);
        if (firstAddress) {
          setShippingAddress(client._id, firstAddress._id);
        }
      }
    });
    setAvailableClients(
      availableClients.filter((client) => !selectedClients.includes(client._id))
    );
    setSelectedClients([]);
  };

  const moveClientsToAvailable = () => {
    const movingClients = assignedClients.filter((client) =>
      selectedClients.includes(client._id)
    );
    setAvailableClients([...availableClients, ...movingClients]);
    setAssignedClients(
      assignedClients.filter((client) => !selectedClients.includes(client._id))
    );
    setSelectedClients([]);
  };

  const canMoveToAssigned = selectedClients.some(id => 
    availableClients.some(client => client._id === id)
  );
  const canMoveToAvailable = selectedClients.some(id => 
    assignedClients.some(client => client._id === id)
  );

  if (isLoading && (!routeData || clients.length === 0)) {
    return (
      <div className="edit-route-clients__initial-loading" aria-busy="true" aria-live="polite">
        <Skeleton className="edit-route-clients__skel-block" />
        <Skeleton className="edit-route-clients__skel-block" />
        <span className="edit-route-clients__sr-only">{t("Cargando datos de la ruta...")}</span>
      </div>
    );
  }

  return (
    <div className="edit-route-clients">
      <div className="edit-route-clients__header">
        <div className="edit-route-clients__header-content">
          <Building2 size={24} className="edit-route-clients__header-icon" />
          <div>
            <h2 className="edit-route-clients__title">
              {t("Gestionar clientes de la ruta")}
            </h2>
            <p className="edit-route-clients__subtitle">
              {routeData?.routeName || t("Sin nombre")}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={closeModal}
          className="edit-route-clients__close-btn"
          aria-label={t("Cerrar")}
        >
          <X size={20} />
        </button>
      </div>

      <div className="edit-route-clients__content">
        <ClientList
          clients={availableClients}
          title={t("Clientes disponibles")}
          selectedClients={selectedClients}
          handleCheckboxChange={handleCheckboxChange}
          isLoading={isLoading}
        />

        <ActionButtons
          moveClientsToAssigned={moveClientsToAssigned}
          moveClientsToAvailable={moveClientsToAvailable}
          canMoveToAssigned={canMoveToAssigned}
          canMoveToAvailable={canMoveToAvailable}
        />

        <ClientList
          clients={assignedClients}
          title={t("Clientes en la ruta")}
          selectedClients={selectedClients}
          handleCheckboxChange={handleCheckboxChange}
          isLoading={isLoading}
        />
      </div>

      <div className="edit-route-clients__footer">
        <Button type="button" variant="outline" onClick={closeModal} disabled={isLoading}>
          {t("Cancelar")}
        </Button>
        <Button
          type="button"
          onClick={submitChange}
          loading={isLoading}
          iconLeft={<Check size={16} />}
        >
          {isLoading ? t("Guardando...") : t("Guardar cambios")}
        </Button>
      </div>
    </div>
  );
}
